import { Injectable, effect, signal } from '@angular/core';
import { map, Observable, of, switchMap, tap } from 'rxjs';
import { INavigationTreenode } from '../types/infrastructure/interfaces/INavigationTreenode';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { AppSettings } from './app.settings';
import { IProjekt } from '../types/IProjekt';
import { IProjektkostenAktuell } from '../types/IProjektkostenAktuell';
import { IProjektkostenPlan } from '../types/IProjektkostenPlan';
import { IProjektantrag } from '../types/IProjektantrag';
import { IControllingbereich } from '../types/IControllingbereich';
import { IPlangebiet } from '../types/IPlangebiet';
import { IQuartier } from '../types/IQuartier';
import { IKlassifizierung } from '../types/IKlassifizierung';
import { IProjektInfo } from '../types/IProjektInfo';
import { ProjektInfo } from '../types/Projektinfo';
import { IBudgetantrag } from '../types/IBudgetantrag';
import { Budgetantrag } from '../types/Budgetantrag';
import { AppstateService } from './appstate.service';
import { IRisikenChancen } from '../types/IRisikenchancen';
import { ProjektantragViewModel } from '../types/viewmodels/projektantrag.viewmodel';
import { IUserInfo } from '../types/IUserinfo';
import { IAuthInfo } from '../types/IAuthInfo';
import { IBudgetantragKosten } from '../types/IBudgetantragKosten';
import { FreigabeViewModel, IFreigabeViewModel } from '../types/viewmodels/freigabe.viewmodel';
import { IFreigabekosten } from '../types/IFreigabekosten';
import { IBudgetantragVerlauf } from '../types/IBudgetantragVerlauf';
import { IFreigabeDetails } from '../types/IFreigabeDetails';
import { IBudgetantragKostenherleitung } from '../types/IBudgetantragKostenherleitung';
import { IKostenherleitungViewModel, KostenherleitungViewModel } from '../types/viewmodels/kostenherleitungViewModel';
import { IBudgetantragViewmodel } from '../types/viewmodels/budgetantrag.viewmodel';
import { instanceToPlain, plainToInstance } from 'class-transformer';
import { IWirtschaftsplan } from '../types/IWirtschaftsplan';
import { IHuAuBau } from '../types/IHuAuBau';
import { IWirtschaftsplanViewmodel } from '../types/viewmodels/wirtschaftsplan.viewmodel';
import { WirtschaftsplanErstellung } from '../types/WirtschaftsplanErstellung';
import { IPreissteigerung } from '../types/IPreissteigerung';
import { IPreissteigerungProzent } from '../types/IPreissteigerungProzent';
import { AuthService } from './auth.service';
import { IBerechnung } from '../types/IBerechnung';
import { BudgetantragKostenherleitung } from '../types/budgetantragKostenherleitung';
import { IFirma } from '../types/IFirma';

@Injectable({
  providedIn: 'root'
})
export class DataService {


  // data caches

  // cache treeview projekte
  public projekteTreeNodes = signal<INavigationTreenode[]>([]);
  // cache treeview archiv
  public archivTreeNodes = signal<INavigationTreenode[]>([]);
  // cache  projekt liste
  public projekte = signal<IProjekt[]>([]);
  // cache  budgetantrag archiv liste
  public budgetantraege = signal<IBudgetantrag[]>([]);
  // cache projektantrag
  public projektAntrag = signal<ProjektantragViewModel>({} as ProjektantragViewModel);
  // cache projektinfo (side panel)
  public projektInfo = signal<IProjektInfo>(ProjektInfo.default);
  // cache for projektdetails
  public projektDetails = signal<IProjekt>({} as IProjekt);
  public projektKostenAktuell = signal<IProjektkostenAktuell[]>([]);
  public projektKostenPlan = signal<IProjektkostenPlan[]>([]);
  public projektKostenAbweichung = signal<IProjektkostenPlan[]>([]);
  // cache for budgetantrag dashboard / details / risiken&Chancen / freigabe
  public budgetantragDetails = signal<IBudgetantrag>({} as IBudgetantrag); // Budgetantrag.default
  public budgetantragDetailsLageplan = signal<File>(new File([], ''));
  public budgetantragKosten = signal<IBudgetantragKosten[] | null>([]);
  public budgetantragRisikenChancen = signal<IRisikenChancen[] | null>(null);
  public budgetantragFreigabe = signal<IFreigabeViewModel>(FreigabeViewModel.default);
  // cache for kostenherleitung
  public budgetantragKostenherleitung = signal<IKostenherleitungViewModel>(KostenherleitungViewModel.default);
  public budgetantragPreissteigerungKosten = signal<IBudgetantragKosten[] | null>(null);
  public budgetantragPreissteigerung = signal<IPreissteigerungProzent[] | null>(null);
  // cache for verlauf
  public budgetantragVerlauf = signal<IBudgetantragVerlauf[]>([]);

  public wirtschaftsplaene = signal<IWirtschaftsplan[]>([]);
  public budgetantragHuAuBa = signal<IHuAuBau[]>([{ huAuBauID: 0, huAuBau: 'Nein' },
  { huAuBauID: 1, huAuBau: 'Geplant' },
  { huAuBauID: 2, huAuBau: 'Liegt vor' }]);

  public budgetantragBerechnung = signal<IBerechnung[]>([{ berechnungID: 0, berechnung: 'Berechnung nach HOAI', bnk: 20, bnk14: 27, bnk59: 73, readonly: true },
  { berechnungID: 1, berechnung: 'Berechnung nach AHO', bnk: 20, bnk14: 40, bnk59: 60, readonly: true },
  { berechnungID: 2, berechnung: 'Eigene Berechnung', bnk: 20, bnk14: 27, bnk59: 73, readonly: false }]);

  public firmen = signal<IFirma[]>([]);
  
  constructor(private httpClient: HttpClient,
    private appSettings: AppSettings,
    private appStateService: AppstateService
  ) {


     // load data of Firmen
    effect(() => {
      this.readFirmen().subscribe((data) => {
        this.firmen.set(data);
      });
    }, { allowSignalWrites: true });
	
	effect(() => {
      var name =  this.projektInfo()?.name || this.projektDetails()?.name || this.budgetantragDetails()?.projektName || '';
      this.appStateService.projektName.set(name);
    },
      { allowSignalWrites: true });
    // load data of Projekt treeview
    effect(() => {
      this.projekteTreeNodes.set([]);
      if (this.appStateService.projekteTreeInit()) {
        this.readNavigationTreeProjekte().subscribe((data) => {
          this.projekteTreeNodes.set(data);
        });
      }
    },
      { allowSignalWrites: true });

    // load data of Budgetantrag archiv treeview
    effect(() => {
      this.archivTreeNodes.set([]);
      if (this.appStateService.archivTreeInit()) {
        this.readNavigationTreeArchiv().subscribe((data) => {
          this.archivTreeNodes.set(data);
        });
      }
    },
      { allowSignalWrites: true });

    // load data of Projekt-Liste
    effect(() => {
      if (this.appStateService.isInitialized()) {
        this.projekte.set([]);
        this.readProjekte(this.appStateService.navigationPlangebiet(),
          this.appStateService.navigationBereich(),
          this.appStateService.navigationBearbeitung()).subscribe((data) => {
            this.projekte.set(data);
          });
      }
    },
      { allowSignalWrites: true }
    )

    // load data of Projektantrag
    effect(() => {
      if (this.appStateService.projektantragInit()) {
        this.readProjektantrag().subscribe((data) => {
          this.projektAntrag.set(data);
        });
      } else {
        this.projektAntrag.set({} as ProjektantragViewModel);
      }
    },
      { allowSignalWrites: true });

    // load data of Budget-Archiv-Liste
    effect(() => {
      this.budgetantraege.set([]);
      this.readBudgetantraege(this.appStateService.navigationPlangebiet(),
        this.appStateService.navigationBereich()).subscribe((data) => {
          this.budgetantraege.set(data);
        })
    },
      { allowSignalWrites: true }
    )

    // load data of ProjektInfo
    // alows to open component multiple times without reloading data
    effect(() => {
      // reset cache
      this.projektInfo.set(ProjektInfo.default);

      // read ProjektInfo when projektId changes
      this.readProjektInfos(this.appStateService.projektInfoId()).subscribe((data) => {
        this.projektInfo.set(data);
      });
    },
      { allowSignalWrites: true }
    );

    // load data of ProjektDetails
    effect(() => {
      // reset cache
      this.projektDetails.set({} as IProjekt);
      this.projektKostenAktuell.set([]);
      this.projektKostenPlan.set([]);
      this.projektKostenAbweichung.set([]);

      // read data when projektId changes
      this.readProjekt(this.appStateService.projektId()).subscribe((data) => {
        this.projektDetails.set(data);
      });
      this.readProjektdetailsKostengruppenAktuell(this.appStateService.projektId()).subscribe((data) => {
        this.projektKostenAktuell.set(data);
      });
      this.readProjektdetailsKostengruppenPlan(this.appStateService.projektId()).subscribe((data) => {
        this.projektKostenPlan.set(data);
      });
      this.readProjektdetailsKostengruppenAbweichung(this.appStateService.projektId()).subscribe((data) => {
        this.projektKostenAbweichung.set(data);
      });
    },
      { allowSignalWrites: true }
    );

    // load data of Budgetantrag
    effect(() => {
      // reset cache
      this.budgetantragDetails.set(Budgetantrag.default);
      this.budgetantragDetailsLageplan.set(new File([], ''));
      this.budgetantragKosten.set(null);
      this.budgetantragKostenherleitung.set(KostenherleitungViewModel.default);
      this.budgetantragRisikenChancen.set(null);
      this.budgetantragFreigabe.set(FreigabeViewModel.default);

      // read data when budgetantragId changes
      this.readBudgetantragViewmodel(this.appStateService.budgetantragId()).subscribe((data) => {
        this.budgetantragDetails.set(data.budgetantrag);
        this.wirtschaftsplaene.set(data.wirtschaftsplaene);
        // read Lageplan, if available
        if (this.budgetantragDetails()?.lageplanDateiname) {
          this.readBudgetantragLageplan(this.appStateService.budgetantragId()).subscribe({
            next: (data) => {
              this.budgetantragDetailsLageplan.set(data);
            }
          });
        }
      });

      // read Kosten und Freigabe
      this.refreshBudgetantragKostenUndFreigabe();

      // read Kostenherleitung
      this.readBudgetantragKostenherleitungen(this.appStateService.budgetantragId()).subscribe((data) => {
        this.budgetantragKostenherleitung.set(data);
      });

      // read RisikenChancen
      this.readRisikenChancen(this.appStateService.budgetantragId()).subscribe((data) => {
        this.budgetantragRisikenChancen.set(data);
      });

      // read RisikenChancen
      this.readBudgetAntragVerlauf(this.appStateService.budgetantragId()).subscribe((data) => {
        this.budgetantragVerlauf.set(data);
      });


    }, { allowSignalWrites: true });

    // load data of Preissteigerung
    effect(() => {
      // reset cache
      this.budgetantragPreissteigerung.set([]);
      this.budgetantragPreissteigerungKosten.set([]);

      // read data when preissteigerungBudgetantragId changes
      this.readBudgetantragPreissteigerung(this.appStateService.budgetantragPreissteigerungId()).subscribe((data) => {
        this.budgetantragPreissteigerung.set(data);
      });
      // read data when preissteigerungBudgetantragId changes
      this.readBudgetantragPreissteigerungKalkulation(this.appStateService.budgetantragPreissteigerungId()).subscribe((data) => {
        this.budgetantragPreissteigerungKosten.set(data);
      });
    }, { allowSignalWrites: true });

   
  }


  readFirmen(): Observable<IFirma[]> {
    return this.httpClient.get<IFirma[]>(this.appSettings.firmenApi).pipe()
  }

  //#region Userinfo

  loginUser(): Observable<HttpResponse<IAuthInfo>> {
    return this.httpClient.get<IAuthInfo>(this.appSettings.loginApi, { observe: 'response' })
  }

  readUserinfo(): Observable<HttpResponse<IUserInfo>> {
    return this.httpClient.get<IUserInfo>(this.appSettings.userinfoApi, { observe: 'response' })
  }

  //#endregion

  //#region Navigation

  readNavigationTreeProjekte(): Observable<INavigationTreenode[]> {
    return this.httpClient.get<INavigationTreenode[]>(this.appSettings.navigationProjekteApi).pipe()
  }

  readNavigationTreeArchiv(): Observable<INavigationTreenode[]> {
    return this.httpClient.get<INavigationTreenode[]>(this.appSettings.navigationBudgetantraegeApi).pipe()
  }

  //#endregion

  //#region Projekte

  readProjekte(plangebiet: string, bereich: string | null, bearbeitung: string | null): Observable<IProjekt[]> {
    if (bereich == null || bearbeitung == null) return of([]);
    return this.httpClient.get<IProjekt[]>(this.appSettings.projekteApi(plangebiet, bereich, bearbeitung)).pipe()
  }

  //#endregion Projekte 

  //#region Projektantrag

  readProjektantrag(): Observable<ProjektantragViewModel> {
    return this.httpClient.get<ProjektantragViewModel>(this.appSettings.projekteAntragApi)
  }

  createProjektantrag(projektantrag: IProjektantrag): Observable<IProjektantrag> {
    return this.httpClient.post<IProjektantrag>(this.appSettings.projekteAntragApi, projektantrag);
  }

  readControllingbereiche(): Observable<IControllingbereich[]> {
    return this.httpClient.get<IControllingbereich[]>(this.appSettings.controllingbereicheApi).pipe()
  }

  readPlangebiete(): Observable<IPlangebiet[]> {
    return this.httpClient.get<IPlangebiet[]>(this.appSettings.plangebieteApi).pipe()
  }

  readQuartiere(): Observable<IQuartier[]> {
    return this.httpClient.get<IQuartier[]>(this.appSettings.quartiereApi).pipe()
  }

  readKlassifizierungen(): Observable<IKlassifizierung[]> {
    return this.httpClient.get<IKlassifizierung[]>(this.appSettings.klassifizierungenApi).pipe()
  }

  //#endregion

  //#region Projektdetails

  readProjekt(projektId: number | null): Observable<IProjekt> {
    if (projektId == null) return of({} as IProjekt);
    return this.httpClient.get<IProjekt>(this.appSettings.projekteDetailsApi(projektId))
  }

  readProjektdetailsKostengruppenAktuell(projektId: number | null): Observable<IProjektkostenAktuell[]> {
    if (projektId == null) return of([]);
    return this.httpClient.get<IProjektkostenAktuell[]>(this.appSettings.projekteDetailsKostengruppenAktuellApi(projektId)).pipe()
  }

  readProjektdetailsKostengruppenPlan(projektId: number | null): Observable<IProjektkostenPlan[]> {
    if (projektId == null) return of([]);
    return this.httpClient.get<IProjektkostenPlan[]>(this.appSettings.projekteDetailsKostengruppenPlanApi(projektId)).pipe()
  }

  readProjektdetailsKostengruppenAbweichung(projektId: number | null): Observable<IProjektkostenPlan[]> {
    if (projektId == null) return of([]);
    return this.httpClient.get<IProjektkostenPlan[]>(this.appSettings.projekteDetailsKostengruppenAbweichungApi(projektId)).pipe()
  }

  readProjektInfos(projektId: number | null): Observable<ProjektInfo> {
    if (projektId == null) return of(ProjektInfo.default);
    return this.httpClient.get<ProjektInfo>(this.appSettings.projekteInfoApi(projektId)).pipe()
  }

  toggleProjektAktiv(projektId: number): Observable<any> {
    return this.httpClient.patch(this.appSettings.projekteDetailsAktivApi(projektId), {}).pipe(
      map((response) => {
        return response;
      })
    );
  }

  //#endregion

  //#region Budgetantrag


  readBudgetantragViewmodel(baId: number | null): Observable<IBudgetantragViewmodel> {
    if (baId == null) return of({} as IBudgetantragViewmodel);
    return this.httpClient.get<IBudgetantragViewmodel>(this.appSettings.budgetantraegeViewmodelApi(baId))
  }

  readBudgetantragDetails(baId: number | null): Observable<IBudgetantrag> {
    if (baId == null) return of({} as IBudgetantrag);
    return this.httpClient.get<IBudgetantrag>(this.appSettings.budgetantraegeDetailsApi(baId))
  }

  readBudgetantragLageplan(baId: number | null): Observable<any> {
    if (baId == null) return of(new File([], ''));
    return this.httpClient.get(this.appSettings.budgetantraegeDetailsLageplanApi(baId), { responseType: 'blob' })
  }

  readBudgetantragNewDefaults(projektId: number): Observable<HttpResponse<IBudgetantragViewmodel>> {
    return this.httpClient.get<IBudgetantragViewmodel>(this.appSettings.budgetantraegeNeuApi(projektId), { observe: 'response' })
  }

  readBudgetantragNewDefaultLageplan(projektId: number): Observable<any> {
    return this.httpClient.get(this.appSettings.budgetantraegeNeuLageplanApi(projektId), { responseType: 'blob' })
  }

  createBudgetantrag(projektId: number, budgetantrag: Budgetantrag): Observable<HttpResponse<Budgetantrag>> {
    return this.httpClient.post<Budgetantrag>(this.appSettings.budgetantraegeNeuApi(projektId), budgetantrag, { observe: 'response' })
      .pipe(
        map((response) => {
          // reload cached projektdetails
          this.appStateService.resetProjektDetails();
          // reload cached projektinfo
          this.appStateService.resetProjektInfo();
          // reload cache of projektliste
          this.appStateService.resetProjektListe();

          return response;
        })
      );

  }

  updateBudgetantrag(baId: number, budgetantrag: Budgetantrag): Observable<HttpResponse<Budgetantrag>> {
    return this.httpClient.patch<Budgetantrag>(this.appSettings.budgetantraegeViewmodelApi(baId), budgetantrag, { observe: 'response' }).pipe(
      map((response) => {
        this.budgetantragDetails.set(response.body!);
        return response;
      })
    );
  }

  deleteBudgetantrag(baId: number): Observable<HttpResponse<Budgetantrag>> {
    return this.httpClient.delete<Budgetantrag>(this.appSettings.budgetantraegeViewmodelApi(baId), { observe: 'response' })
      .pipe(
        map((response) => {
          // reload cached projektdetails
          this.appStateService.resetProjektDetails();
          // reload cached projektinfo
          this.appStateService.resetProjektInfo();
          // reload cache of projektliste
          this.appStateService.resetProjektListe();

          return response;
        })
      );
  }

  updateBudgetantragLageplan(baId: number, formData: FormData): Observable<string> {
    return this.httpClient.patch<any>(this.appSettings.budgetantraegeDetailsLageplanApi(baId), formData).pipe(
      map((response) => {
        this.budgetantragDetailsLageplan.set(formData.get('file') as File);
        return response.filePath;
      })
    );
  }

  deleteBudgetantragLageplan(baId: number): Observable<boolean> {
    return this.httpClient.delete<boolean>(this.appSettings.budgetantraegeDetailsLageplanApi(baId)).pipe(
      map((response) => {
        this.budgetantragDetailsLageplan.set(new File([], ''));
        return response;
      })
    );
  }

  //#endregion

  //#region Kosten

  readBudgetantragKosten(baId: number | null): Observable<IBudgetantragKosten[]> {
    if (baId == null) return of([]);
    return this.httpClient.get<IBudgetantragKosten[]>(this.appSettings.budgetantraegeKostenApi(baId))
  }

  readBudgetantragKostenherleitungen(baId: number | null): Observable<IKostenherleitungViewModel> {
    if (baId == null) return of(KostenherleitungViewModel.default)
    return this.httpClient.get<IKostenherleitungViewModel>(this.appSettings.budgetantraegeKostenherleitungenApi(baId)).pipe(
      map((response) => {
        response = plainToInstance(KostenherleitungViewModel, response as Object);
        if (response != null && response.kostenherleitungen != null) {
          response.kostenherleitungen.forEach(element => {
            // end of initializing
            element.isInitialized = true;
          });
        }
        return response;
      })
    );
  }

  updateBudgetantragKostenherleitungenAuftragswerte(baId: number | null): Observable<IKostenherleitungViewModel> {
    return this.httpClient.post(this.appSettings.budgetantraegeKostenherleitungenUpdateAuftragswerteApi(baId!), {}).pipe(
      switchMap(() => {
        return this.readBudgetantragKostenherleitungen(baId);
      }),
      tap((data) => {
        this.budgetantragKostenherleitung.set(data);
      })
    );
  }


  createBudgetantragKostenherleitung(baId: number | null, kostenherleitung: IBudgetantragKostenherleitung): Observable<IBudgetantragKostenherleitung> {
    var plainKostenherleitung = instanceToPlain(kostenherleitung, { excludePrefixes: ['_', 'kostenHerleitungId'] });
    return this.httpClient.post<IBudgetantragKostenherleitung>(this.appSettings.budgetantraegeKostenherleitungenApi(baId!), plainKostenherleitung).pipe(
      map((response) => {
        this.refreshBudgetantragKostenUndFreigabe();
        response.isInitialized = true;
        return plainToInstance(BudgetantragKostenherleitung, response);
      })
    );
  }

  createBudgetantragKostenherleitungen(baId: number | null, kostenherleitungen: IBudgetantragKostenherleitung[]): Observable<IBudgetantragKostenherleitung[]> {
    var plainKostenherleitungen = instanceToPlain(kostenherleitungen, { excludePrefixes: ['_', 'kostenHerleitungId'] });
    return this.httpClient.post<IBudgetantragKostenherleitung[]>(this.appSettings.budgetantraegeKostenherleitungenTeilleistungeApi(baId!), plainKostenherleitungen);
  }

  updateBudgetantragKostenherleitung(baId: number | null, kostenherleitungId: number, kostenherleitung: IBudgetantragKostenherleitung): Observable<IBudgetantragKostenherleitung> {
    var plainKostenherleitung = instanceToPlain(kostenherleitung, { excludePrefixes: ['_'] });
    return this.httpClient.patch<IBudgetantragKostenherleitung>(this.appSettings.budgetantraegeKostenherleitungDetailApi(baId!, kostenherleitungId), plainKostenherleitung).pipe(
      map((response) => {
        this.refreshBudgetantragKostenUndFreigabe();
        return response;
      })
    );
  }

  deleteBudgetantragKostenherleitung(baId: number | null, kostenherleitungId: number): Observable<boolean> {
    return this.httpClient.delete<boolean>(this.appSettings.budgetantraegeKostenherleitungDetailApi(baId!, kostenherleitungId)).pipe(
      map((response) => {
        this.refreshBudgetantragKostenUndFreigabe();
        return response;
      })
    );
  }

  refreshBudgetantragKostenUndFreigabe() {
    // Neuberechnung der Kosten für das Dashboard
    this.readBudgetantragKosten(this.appStateService.budgetantragId()).subscribe((data) => {
      this.budgetantragKosten.set(data);
    });
    this.readFreigabe(this.appStateService.budgetantragId()).subscribe((data) => {
      this.budgetantragFreigabe.set(data);
    });
  }

  refreshBudgetantragKostenherleitung() {
    this.readBudgetantragKostenherleitungen(this.appStateService.budgetantragId()).subscribe((data) => {
      this.budgetantragKostenherleitung.set(data);
    });
  }


  refreshBudgetantragDetails() {
    this.readBudgetantragViewmodel(this.appStateService.budgetantragId()).subscribe((data) => {
      this.budgetantragDetails.set(data.budgetantrag);
    })
  }

  readBudgetantragKostenherleitungKalkulation(baId: number | null, kostenherleitungId: number): Observable<any> {
    if (baId == null) return of(new File([], ''));
    return this.httpClient.get(this.appSettings.budgetantraegeKostenherleitungDetailAnhangApi(baId, kostenherleitungId), { responseType: 'blob' })
  }

  updateBudgetantragKostenherleitungKalkulation(baId: number, kostenherleitungId: number, formData: FormData): Observable<boolean> {
    return this.httpClient.patch<boolean>(this.appSettings.budgetantraegeKostenherleitungDetailAnhangApi(baId, kostenherleitungId), formData);
  }

  deleteBudgetantragKostenherleitungKalkulation(baId: number, kostenherleitungId: number): Observable<boolean> {
    return this.httpClient.delete<boolean>(this.appSettings.budgetantraegeKostenherleitungDetailAnhangApi(baId, kostenherleitungId));
  }

  readBudgetantragPreissteigerung(baId: number | null): Observable<IPreissteigerungProzent[]> {
    if (baId == null) return of([]);
    return this.httpClient.get<IPreissteigerungProzent[]>(this.appSettings.budgetantraegePreissteigerungApi(baId,));
  }

  readBudgetantragPreissteigerungKalkulation(baId: number | null): Observable<IBudgetantragKosten[]> {
    if (baId == null) return of([]);
    return this.httpClient.get<IBudgetantragKosten[]>(this.appSettings.budgetantraegePreissteigerungKostenApi(baId));
  }

  updateBudgetantragPreissteigerung(baId: number | null, preissteigerung: IPreissteigerung): Observable<IBudgetantragKosten[]> {
    if (baId == null) return of([]);

    return this.httpClient.patch<IBudgetantragKosten[]>(this.appSettings.budgetantraegePreissteigerungApi(baId), preissteigerung).pipe(
      map((response) => {
        this.budgetantragPreissteigerungKosten.set(response);
        this.refreshBudgetantragKostenUndFreigabe();
        return response;
      })
    );
  }

  readBudgetAntragVerlauf(baId: number | null): Observable<IBudgetantragVerlauf[]> {
    if (baId == null) return of([]);
    return this.httpClient.get<IBudgetantragVerlauf[]>(this.appSettings.budgetantraegeVerlaufApi(baId))
  }

  //#endregion

  //#region RisikenChancen

  readRisikenChancen(baId: number | null): Observable<IRisikenChancen[]> {
    if (baId == null) return of([]);
    return this.httpClient.get<IRisikenChancen[]>(this.appSettings.budgetantraegeRisikenChancenApi(baId))
  }

  //#endregion

  //#region Freigabe

  readFreigabe(baId: number | null): Observable<IFreigabeViewModel> {
    if (baId == null) return of(FreigabeViewModel.default);
    return this.httpClient.get<IFreigabeViewModel>(this.appSettings.budgetantraegeFreigabeApi(baId))
  }

  readFreigabeUrsachen(baId: number | null): Observable<IFreigabeDetails> {
    return this.httpClient.get<IFreigabeDetails>(this.appSettings.budgetantraegeFreigabeUrsachenApi(baId!))
  }

  readFreigabeAnhang(baId: number | null, freigabestufe: string): Observable<any> {
    if (baId == null) return of(new File([], ''));
    return this.httpClient.get(this.appSettings.budgetantraegeFreigabeAnhangApi(baId, freigabestufe), { responseType: 'blob' })
  }

  updateFreigabeAnhang(baId: number, freigabestufe: string, formData: FormData): Observable<boolean> {
    return this.httpClient.patch<boolean>(this.appSettings.budgetantraegeFreigabeAnhangApi(baId, freigabestufe), formData);
  }

  deleteFreigabeAnhang(baId: number, freigabestufe: string): Observable<boolean> {
    return this.httpClient.delete<boolean>(this.appSettings.budgetantraegeFreigabeAnhangApi(baId, freigabestufe));
  }

  updateFreigabeDetails(baId: number, details: IFreigabeDetails): Observable<HttpResponse<IFreigabeViewModel>> {
    return this.httpClient.patch<IFreigabeViewModel>(this.appSettings.budgetantraegeFreigabeApi(baId), details, { observe: 'response' });
  }

  updateFreigabeApply(baId: number, details: IFreigabeDetails): Observable<HttpResponse<IFreigabeViewModel>> {
    return this.httpClient.patch<IFreigabeViewModel>(this.appSettings.budgetantraegeFreigabeApplyApi(baId), details, { observe: 'response' }).pipe(
      map((response) => {
        this.refreshFreigabeVerlauf();
        return response;
      })
    );
  }

  updateFreigabeReject(baId: number, details: IFreigabeDetails): Observable<HttpResponse<IFreigabeViewModel>> {
    return this.httpClient.patch<IFreigabeViewModel>(this.appSettings.budgetantraegeFreigabeRejectApi(baId), details, { observe: 'response' }).pipe(
      map((response) => {
        this.refreshFreigabeVerlauf();
        return response;
      })
    );
  }

  updateFreigabeAufheben(baId: number): Observable<HttpResponse<IFreigabeViewModel>> {
    return this.httpClient.patch<IFreigabeViewModel>(this.appSettings.budgetantraegeFreigabeAufhebenApi(baId), {}, { observe: 'response' }).pipe(
      map((response) => {
        this.refreshFreigabeVerlauf();
        return response;
      })
    );
  }

  refreshFreigabeVerlauf() {
    // read RisikenChancen
    this.readBudgetAntragVerlauf(this.appStateService.budgetantragId()).subscribe((data) => {
      this.budgetantragVerlauf.set(data);
    });
  }

  //#endregion

  //#region Budgetanträge

  readBudgetantraege(plangebiet: string, bereich: string | null): Observable<Budgetantrag[]> {
    if (bereich == null) return of([]);
    return this.httpClient.get<Budgetantrag[]>(this.appSettings.budgetantraegeApi(plangebiet, bereich)).pipe()
  }

  //#endregion

  //#region Wirtschaftsplan

  readWirtschaftsplanViewModel(): Observable<IWirtschaftsplanViewmodel> {
    return this.httpClient.get<IWirtschaftsplanViewmodel>(this.appSettings.wirtschaftsplanApiBase).pipe()
  }

  createWirtschaftsplan(wirtschaftsplan: WirtschaftsplanErstellung): Observable<IWirtschaftsplan> {
    return this.httpClient.post<IWirtschaftsplan>(this.appSettings.wirtschaftsplanApiBase, wirtschaftsplan).pipe()
  }

  deleteWirtschaftsplan(wirtschaftsplan: WirtschaftsplanErstellung): Observable<IWirtschaftsplan> {
    return this.httpClient.post<IWirtschaftsplan>(this.appSettings.wirtschaftsplanLoeschenApi, wirtschaftsplan).pipe()
  }

  //#endregion

  //#region Reporting

  generateReport(budgetAntragID: number): Observable<any> {
    return this.httpClient.get<any>(this.appSettings.budgetantraegeReportGenerateApi(budgetAntragID)).pipe()
  }

  getReport(budgetAntragID: number): Observable<any> {
    return this.httpClient.get<any>(this.appSettings.budgetantraegeReportApi(budgetAntragID)).pipe()
  }

  //#endregion


  postTest(testArt: string): void {
    this.httpClient.post<any>(this.appSettings.infoTestApi(testArt), {}).pipe().subscribe((response) => {
    });
  }

}
